import React, { useState, useEffect } from "react";
import {
  collection, addDoc, setDoc, doc, onSnapshot, query, where, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { connect } from "react-redux";
import { Link } from "react-router-dom";

function Groups({ first_name, last_name, profile_pic_url }) {
  const userId = localStorage.getItem("user_id");
  const [groups, setGroups] = useState([]);
  const [myMemberships, setMyMemberships] = useState([]);
  const [memberCounts, setMemberCounts] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const unsubGroups = onSnapshot(collection(db, "groups"), snap => {
      setGroups(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.created_at?.toMillis?.() || 0) - (a.created_at?.toMillis?.() || 0))
      );
    });

    const unsubCounts = onSnapshot(
      query(collection(db, "group_members"), where("status", "==", "approved")),
      snap => {
        const counts = {};
        snap.docs.forEach(d => {
          const gid = d.data().group_id;
          counts[gid] = (counts[gid] || 0) + 1;
        });
        setMemberCounts(counts);
      }
    );

    const unsubMine = onSnapshot(
      query(collection(db, "group_members"), where("user_id", "==", userId)),
      snap => setMyMemberships(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    return () => { unsubGroups(); unsubCounts(); unsubMine(); };
  }, [userId]);

  const createGroup = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const groupRef = await addDoc(collection(db, "groups"), {
        name: name.trim(),
        description: description.trim(),
        creator_user_id: userId,
        created_at: serverTimestamp(),
      });
      await setDoc(doc(db, "group_members", `${userId}_${groupRef.id}`), {
        group_id: groupRef.id,
        user_id: userId,
        first_name: first_name || "",
        last_name: last_name || "",
        profile_pic_url: profile_pic_url || null,
        role: "admin",
        status: "approved",
        joined_at: serverTimestamp(),
      });
      setName("");
      setDescription("");
      setShowCreate(false);
    } finally {
      setSubmitting(false);
    }
  };

  const requestJoin = async (groupId) => {
    await setDoc(doc(db, "group_members", `${userId}_${groupId}`), {
      group_id: groupId,
      user_id: userId,
      first_name: first_name || "",
      last_name: last_name || "",
      profile_pic_url: profile_pic_url || null,
      role: "member",
      status: "pending",
      joined_at: serverTimestamp(),
    });
  };

  const getMembership = (groupId) => myMemberships.find(m => m.group_id === groupId);

  const boxStyle = {
    background: "rgba(255,255,255,0.88)",
    borderRadius: "16px",
    width: "85%",
    maxWidth: "640px",
    margin: "0 auto 20px",
    boxShadow: "0 2px 10px rgba(13,59,140,0.1)",
    padding: "20px",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F0F7FF", paddingBottom: "40px" }}>
      <br /><br />
      <h2>Groups</h2>

      <div style={{ ...boxStyle }}>
        {!showCreate ? (
          <div style={{ textAlign: "center" }}>
            <button
              className="brownroundbutton"
              onClick={() => setShowCreate(true)}
              style={{ cursor: "pointer" }}
            >
              + Create Group
            </button>
          </div>
        ) : (
          <>
            <p style={{ fontFamily: "Hebrew", fontSize: "1.6rem", fontWeight: "bold", color: "#0D3B8C", marginTop: 0 }}>
              New Group
            </p>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Group name"
              style={{ width: "100%", boxSizing: "border-box", padding: "10px 14px", fontSize: "15px", borderRadius: "12px", border: "2px solid #DBEAFE", fontFamily: "Hebrew", marginBottom: "10px", outline: "none" }}
            />
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Description (optional)"
              rows={3}
              style={{ width: "100%", boxSizing: "border-box", padding: "10px 14px", fontSize: "15px", borderRadius: "12px", border: "2px solid #DBEAFE", fontFamily: "Hebrew", resize: "none", outline: "none", marginBottom: "12px" }}
            />
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                className="brownroundbutton"
                onClick={() => { setShowCreate(false); setName(""); setDescription(""); }}
                style={{ cursor: "pointer", background: "#888", border: "2px solid #666" }}
              >
                Cancel
              </button>
              <button
                className="brownroundbutton"
                onClick={createGroup}
                disabled={submitting || !name.trim()}
                style={{ cursor: "pointer" }}
              >
                {submitting ? "Creating…" : "Create"}
              </button>
            </div>
          </>
        )}
      </div>

      {groups.length === 0 && (
        <p style={{ fontFamily: "Hebrew", fontSize: "1.4rem", color: "#999", fontStyle: "italic", textAlign: "center" }}>
          No groups yet — be the first to create one!
        </p>
      )}

      {groups.map(group => {
        const membership = getMembership(group.id);
        const count = memberCounts[group.id] || 0;
        return (
          <div key={group.id} style={{ ...boxStyle }}>
            <p style={{ fontFamily: "Hebrew", fontSize: "1.8rem", fontWeight: "bold", color: "#0D3B8C", margin: "0 0 4px" }}>
              {group.name}
            </p>
            <p style={{ fontFamily: "Hebrew", fontSize: "1.2rem", color: "#888", margin: "0 0 8px" }}>
              {count} member{count !== 1 ? "s" : ""}
            </p>
            {group.description && (
              <p style={{ fontFamily: "Hebrew", fontSize: "1.4rem", color: "#444", margin: "0 0 14px", lineHeight: "1.5" }}>
                {group.description}
              </p>
            )}
            <div>
              {membership?.status === "approved" ? (
                <Link to={`/group/${group.id}`}>
                  <button className="brownroundbutton" style={{ cursor: "pointer" }}>Enter Group</button>
                </Link>
              ) : membership?.status === "pending" ? (
                <span style={{ fontFamily: "Hebrew", fontSize: "1.3rem", color: "#F59E0B", fontStyle: "italic" }}>
                  Membership request pending…
                </span>
              ) : (
                <button
                  className="brownroundbutton"
                  onClick={() => requestJoin(group.id)}
                  style={{ cursor: "pointer", background: "#22C55E", border: "2px solid #16A34A" }}
                >
                  Request to Join
                </button>
              )}
            </div>
          </div>
        );
      })}
      <br />
    </div>
  );
}

const mapStateToProps = (state) => ({
  first_name: state.first_name,
  last_name: state.last_name,
  profile_pic_url: state.profile_pic_url,
});

export default connect(mapStateToProps, {})(Groups);
